import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/layout/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import IncomePage from "./pages/IncomePage";
import ExpensesPage from "./pages/ExpensesPage";
import FinanceOverviewPage from "./pages/FinanceOverviewPage";
import FinanceApprovalsPage from "./pages/FinanceApprovalsPage";
import BudgetsPage from "./pages/BudgetsPage";
import YearArchivePage from "./pages/YearArchivePage";
import SchoolFeesPage from "./pages/SchoolFeesPage";
import SalariesPage from "./pages/SalariesPage";
import StaffPage from "./pages/StaffPage";
import LoansPage from "./pages/LoansPage";
import UsersPage from "./pages/UsersPage";
import MaizeProgramPage from "./pages/MaizeProgramPage";
import ChildSponsorshipPage from "./pages/ChildSponsorshipPage";
import SponsorshipAccountabilityPage from "./pages/SponsorshipAccountabilityPage";
import VocationalTrainingPage from "./pages/VocationalTrainingPage";
import PersonnelStaffPage from "./pages/PersonnelStaffPage";
import StudentsPage from "./pages/StudentsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import AssetsDashboardPage from "./pages/AssetsDashboardPage";
import AssetsListPage from "./pages/AssetsListPage";
import InventoryPage from "./pages/InventoryPage";
import MaintenancePage from "./pages/MaintenancePage";
import SchoolClubsPage from "./pages/SchoolClubsPage";
import CatchupLearningPage from "./pages/CatchupLearningPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminAuditLogPage from "./pages/AdminAuditLogPage";
import DocumentsPage from "./pages/DocumentsPage";
import AdminDataEntryAccessPage from "./pages/AdminDataEntryAccessPage";
import AdminDataManagementPage from "./pages/AdminDataManagementPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import DataEntryCenterPage from "./pages/DataEntryCenterPage";
import RequisitionsPage from "./pages/RequisitionsPage";
import SISStudentsPage from "./pages/SISStudentsPage";
import SISStudentProfilePage from "./pages/SISStudentProfilePage";
import StaffManagementPage from "./pages/StaffManagementPage";
import StaffProfilePage from "./pages/StaffProfilePage";
import WomenOverviewPage from "./pages/WomenOverviewPage";
import RequirePerm from "./components/RequirePerm";

const P = (module, el) => <RequirePerm module={module}>{el}</RequirePerm>;

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Route */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/invite/:token" element={<InviteAcceptPage />} />

            {/* Protected Routes */}
            <Route element={<Layout title="Dashboard" />}>
              <Route path="/dashboard" element={<DashboardPage />} />
            </Route>

            {/* Finance Module */}
            <Route element={<Layout title="Finance · Overview" />}>
              <Route path="/finance/overview" element={P('finance', <FinanceOverviewPage />)} />
            </Route>
            <Route element={<Layout title="Finance · Income" />}>
              <Route path="/finance/income" element={P('finance', <IncomePage />)} />
              {/* Legacy path compat */}
              <Route path="/income" element={P('finance', <IncomePage />)} />
            </Route>
            <Route element={<Layout title="Finance · Expenses" />}>
              <Route path="/finance/expenses" element={P('finance', <ExpensesPage />)} />
              <Route path="/expenses" element={P('finance', <ExpensesPage />)} />
            </Route>
            <Route element={<Layout title="Finance · Requisitions & Approvals" />}>
              <Route path="/finance/approvals" element={P('requisitions', <FinanceApprovalsPage />)} />
            </Route>
            <Route element={<Layout title="Finance · Budgets" />}>
              <Route path="/finance/budgets" element={P('finance', <BudgetsPage />)} />
            </Route>
            <Route element={<Layout title="Finance · Year Archive" />}>
              <Route path="/finance/archive" element={P('finance', <YearArchivePage />)} />
            </Route>
            <Route element={<Layout title="Schools · School Fees Collection" />}>
              <Route path="/schools/fees" element={P('school_fees', <SchoolFeesPage />)} />
            </Route>
            <Route element={<Layout title="Finance · Salaries" />}>
              <Route path="/finance/salaries" element={P('payroll', <SalariesPage />)} />
            </Route>

            {/* Personnel Module — Phase 4 Staff Mgmt replaces legacy */}
            <Route element={<Layout title="Staff Management" />}>
              <Route path="/personnel/staff" element={P('personnel', <PersonnelStaffPage />)} />
              <Route path="/staff" element={P('personnel', <PersonnelStaffPage />)} />
            </Route>
            <Route element={<Layout title="Staff Profile" />}>
              <Route path="/staff/:staffId" element={P('personnel', <StaffProfilePage />)} />
              <Route path="/personnel/staff/:staffId" element={P('personnel', <StaffProfilePage />)} />
            </Route>
            <Route element={<Layout title="Personnel · Students" />}>
              <Route path="/personnel/students" element={P('students', <StudentsPage />)} />
            </Route>

            {/* Legacy user-account-linked staff list */}
            <Route element={<Layout title="Staff Accounts" />}>
              <Route path="/staff-accounts" element={<StaffPage />} />
            </Route>

            {/* Performance page removed — integrated into individual staff profiles */}

            {/* Projects Module - individual program pages */}
            <Route element={<Layout title="Loans Program" />}>
              <Route path="/loans" element={P('loans', <LoansPage />)} />
            </Route>
            <Route element={<Layout title="Women Program Overview" />}>
              <Route path="/women/overview" element={P('projects', <WomenOverviewPage />)} />
            </Route>
            <Route element={<Layout title="Maize Program" />}>
              <Route path="/maize" element={P('maize', <MaizeProgramPage />)} />
            </Route>
            <Route element={<Layout title="Sponsorship Accountability" />}>
              <Route path="/sponsorship" element={P('sponsorship', <SponsorshipAccountabilityPage />)} />
              <Route path="/sponsorship/legacy" element={P('sponsorship', <ChildSponsorshipPage />)} />
            </Route>
            <Route element={<Layout title="Vocational Training" />}>
              <Route path="/training" element={P('training', <VocationalTrainingPage />)} />
            </Route>
            <Route element={<Layout title="School Clubs" />}>
              <Route path="/school-clubs" element={P('school_clubs', <SchoolClubsPage />)} />
            </Route>
            <Route element={<Layout title="Catch-up Learning" />}>
              <Route path="/catchup" element={P('catchup', <CatchupLearningPage />)} />
            </Route>
            <Route element={<Layout title="Project Details" />}>
              <Route path="/projects/:projectId" element={P('projects', <ProjectDetailPage />)} />
            </Route>
            {/* Redirect old /projects list page to dashboard */}
            <Route path="/projects" element={<Navigate to="/dashboard" replace />} />

            <Route element={<Layout title="Users" />}>
              <Route path="/users" element={P('user_mgmt', <UsersPage />)} />
            </Route>

            {/* Super Admin Module */}
            <Route element={<Layout title="Admin · User Management" />}>
              <Route path="/admin/users" element={<AdminUsersPage />} />
            </Route>
            <Route element={<Layout title="Admin · Audit Log" />}>
              <Route path="/admin/audit" element={<AdminAuditLogPage />} />
              <Route path="/documents" element={P('documents', <DocumentsPage />)} />
            </Route>
            <Route element={<Layout title="Admin · Data Entry Access" />}>
              <Route path="/admin/data-entry-access" element={<AdminDataEntryAccessPage />} />
            </Route>
            <Route element={<Layout title="Admin · Data Management" />}>
              <Route path="/admin/data-management" element={<AdminDataManagementPage />} />
            </Route>

            {/* Data Entry Center (Phase 2) */}
            <Route element={<Layout title="Data Entry Center" />}>
              <Route path="/data-entry" element={P('data_entry', <DataEntryCenterPage />)} />
            </Route>
            <Route element={<Layout title="Operational Requisitions" />}>
              <Route path="/requisitions" element={P('requisitions', <RequisitionsPage />)} />
            </Route>

            {/* Phase 3: Student Information System */}
            <Route element={<Layout title="Student Directory" />}>
              <Route path="/sis/students" element={<Navigate to="/sis/students/primary" replace />} />
              <Route path="/sis/students/primary" element={P('students', <SISStudentsPage key="primary" school="suubi_primary" />)} />
              <Route path="/sis/students/secondary" element={P('students', <SISStudentsPage key="secondary" school="suubi_secondary" />)} />
            </Route>
            <Route element={<Layout title="Student Profile" />}>
              <Route path="/sis/students/:studentId" element={P('students', <SISStudentProfilePage />)} />
            </Route>

            {/* Assets & Inventory Module */}
            <Route element={<Layout title="Asset Dashboard" />}>
              <Route path="/assets/dashboard" element={P('assets', <AssetsDashboardPage />)} />
            </Route>
            <Route element={<Layout title="Asset Register" />}>
              <Route path="/assets/list" element={P('assets', <AssetsListPage />)} />
            </Route>
            <Route element={<Layout title="Inventory" />}>
              <Route path="/assets/inventory" element={P('assets', <InventoryPage />)} />
            </Route>
            <Route element={<Layout title="Maintenance" />}>
              <Route path="/assets/maintenance" element={P('assets', <MaintenancePage />)} />
            </Route>
            <Route path="/assets" element={<Navigate to="/assets/dashboard" replace />} />

            {/* Default Redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
